/** Encode ArrayBuffer → base64url string */
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let str = ''
  for (const byte of bytes) str += String.fromCharCode(byte)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/** Decode base64url string → ArrayBuffer */
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  const buffer = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) buffer[i] = binary.charCodeAt(i)
  return buffer.buffer
}

/**
 * Serialize a PublicKeyCredential created/gotten from the browser
 * into a JSON string that the server can deserialize.
 */
function serializeCredential(credential) {
  const response = credential.response

  const obj: { id: string, type: string, rawId: string, response: any } = {
    id: credential.id,
    type: credential.type,
    rawId: bufferToBase64url(credential.rawId),
    response: {}
  }

  // Registration response
  if (response.attestationObject) {
    obj.response.clientDataJSON = bufferToBase64url(response.clientDataJSON)
    obj.response.attestationObject = bufferToBase64url(
      response.attestationObject
    )
  }

  // Authentication response
  if (response.authenticatorData) {
    obj.response.clientDataJSON = bufferToBase64url(response.clientDataJSON)
    obj.response.authenticatorData = bufferToBase64url(
      response.authenticatorData
    )
    obj.response.signature = bufferToBase64url(response.signature)
    obj.response.userHandle = response.userHandle
      ? bufferToBase64url(response.userHandle)
      : null
  }

  return JSON.stringify(obj)
}

/**
 * Convert a PublicKeyCredentialCreationOptions JSON object (from the server)
 * into the format expected by navigator.credentials.create().
 */
function prepareCreationOptions(options) {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id)
    },
    excludeCredentials: (options.excludeCredentials ?? []).map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id)
    }))
  }
}

/**
 * Convert a PublicKeyCredentialRequestOptions JSON object (from the server)
 * into the format expected by navigator.credentials.get().
 */
function prepareRequestOptions(options) {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials ?? []).map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id)
    }))
  }
}

/**
 * WebAuthn utility functions for browser WebAuthn API
 */
export const registerPasskey = async (options: any): Promise<any> => {
  if (!window.navigator.credentials) {
    throw new Error('WebAuthn is not supported in this browser')
  }

  try {
    const credential = await navigator.credentials.create({
      publicKey: prepareCreationOptions(options)
    })

    if (credential == null) throw new Error('No credential returned by the browser.')

    return serializeCredential(credential)
  } catch (error) {
    console.error('WebAuthn registration error:', error)
    throw error
  }
}

export const authenticateWithPasskey = async (options: any): Promise<any> => {
  if (!window.navigator.credentials) {
    throw new Error('WebAuthn is not supported in this browser')
  }

  try {
    const credential = await navigator.credentials.get({
      publicKey: prepareRequestOptions(options)
    })

    return serializeCredential(credential)
  } catch (error) {
    console.error('WebAuthn authentication error:', error)
    throw error
  }
}
